import React, { useState, useEffect } from "react";
import { Button, Card, CardContent, CardHeader, Grid } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { getProxyApplicationUsagePeriods } from "../clients/FixieApiClient";
import UsagePeriodsTable from "../components/UsagePeriodsTable";
import DashboardPage from "../layouts/DashboardPage";
import UsagePeriodsEmptyState from "../components/UsagePeriodsEmptyState";
import PageLoadingSpinner from "../components/PageLoadingSpinner";
import UsageChart, { ChartData } from "../components/UsageChart";
import FormatBandwidth from "../components/FormatBandwidth";
import FormatNumber from "../components/FormatNumber";

export default () => {
  const { id } = useParams();
  const [usagePeriods, setUsagePeriods] = useState([]);
  const [requestUsagePeriodsForChart, setRequestUsagePeriodsForChart] = useState<ChartData[]>([]);
  const [bandwidthUsagePeriodsForChart, setBandwidthUsagePeriodsForChart] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const transformDataForChart = (data: [any], key: 'requests' | 'bandwidth') =>
    data
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((entry) => ({
        date: new Date(entry.createdAt),
        value: entry.usage[key],
      }));

  useEffect(() => {
    const fetchUsagePeriods = async () => {
      setIsLoading(true);
      setUsagePeriods([]);
      try {
        const usagePeriodResponse = await getProxyApplicationUsagePeriods(id!);
        if (usagePeriodResponse.ok) {
          const usagePeriodResponseData = await usagePeriodResponse.json()
          setUsagePeriods(usagePeriodResponseData);
          setRequestUsagePeriodsForChart(transformDataForChart(usagePeriodResponseData, 'requests'));
          setBandwidthUsagePeriodsForChart(transformDataForChart(usagePeriodResponseData, 'bandwidth'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsagePeriods();
  }, [id]);

  const valueFormatter = (value: number | null, key: string) => {
    if (key === 'bandwidth' && value !== null) {
      return <FormatBandwidth bytes={value} />
    } else {
      return (value && <FormatNumber value={value} />)
    }
  }

  const historyGraphsAndTable = () => {
    return (
      <Grid container spacing={2} justifyContent="space-between" alignItems="center" padding="1rem">
        <Grid item xs={6}>
          <Card sx={{ marginBottom: '1rem' }}>
            <CardHeader title="Request Usage" />
            <CardContent>
              {requestUsagePeriodsForChart.length > 0 && !isLoading && <UsageChart data={requestUsagePeriodsForChart} label="Requests" yKey="requests" yAxisValueFormatter={valueFormatter} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card sx={{ marginBottom: '1rem' }}>
            <CardHeader title="Bandwidth Usage" />
            <CardContent>
              {bandwidthUsagePeriodsForChart.length > 0 && !isLoading && <UsageChart data={bandwidthUsagePeriodsForChart} label="Bandwidth" yKey="bandwidth" yAxisValueFormatter={valueFormatter} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ marginBottom: '1rem' }}>
            <CardHeader title="Summary" />
            <CardContent>
              <UsagePeriodsTable usagePeriods={usagePeriods} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  return (
    <DashboardPage 
      title="History"
      headerChildren={<Button component={Link} to={`/apps/${id}`}>← Details</Button>}
      isLoading={isLoading}
    >
      {isLoading ? <PageLoadingSpinner /> : <div></div>}
      {usagePeriods.length === 0 && !isLoading && <UsagePeriodsEmptyState />}
      {usagePeriods.length > 0 && !isLoading && historyGraphsAndTable()}
    </DashboardPage>
  );
}
